/**
 * Profile.js — My Profile page
 *
 * Validations added:
 * 1. First name: required, min 2, max 50, letters/spaces/hyphens only
 * 2. Last name: optional, but if entered: min 2, max 50, letters/spaces/hyphens
 * 3. Phone: optional, if entered: valid Indian/international format
 * 4. Password: all 3 fields get inline errors (not just toast)
 *    - Current: required
 *    - New: min 8, must contain letter + number
 *    - Confirm: must match new exactly
 *
 * Existing fixes preserved:
 * - Blank page after save fix (re-fetch after PUT)
 * - "Joined Invalid Date" fix (local date parser)
 * - Avatar initials fix (merge API response)
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Form, Button, Spinner,
    Alert, Badge, InputGroup,
} from 'react-bootstrap';
import {
    User, Building2, Mail, Phone, Calendar,
    Key, Save, Eye, EyeOff, BadgeCheck,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import AvatarUpload from '../../components/common/AvatarUpload';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (val) => {
    if (!val) return null;
    const s = String(val).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m-1, d).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
    });
};

const initials = (first, last) => {
    const f = (first || '').charAt(0).toUpperCase();
    const l = (last  || '').charAt(0).toUpperCase();
    return (f + l) || '?';
};

// ── Profile form validation ───────────────────────────────────────────────────
const validateProfile = (form) => {
    const e = {};
    const NAME_REGEX = /^[a-zA-Z\s\-'.]+$/;

    const fn = form.first_name?.trim();
    if (!fn)                    e.first_name = 'First name is required';
    else if (fn.length < 2)     e.first_name = 'First name must be at least 2 characters';
    else if (fn.length > 50)    e.first_name = 'First name cannot exceed 50 characters';
    else if (!NAME_REGEX.test(fn)) e.first_name = 'First name should contain only letters';

    const ln = form.last_name?.trim();
    if (ln) {
        if (ln.length < 2)       e.last_name = 'Last name must be at least 2 characters';
        else if (ln.length > 50) e.last_name = 'Last name cannot exceed 50 characters';
        else if (!NAME_REGEX.test(ln)) e.last_name = 'Last name should contain only letters';
    }

    const ph = form.phone?.trim();
    if (ph) {
        // Accepts: +91-9611892030, +919611892030, 9611892030, 09611892030, +1-800-555-1234
        const PHONE_REGEX = /^[+]?[\d\s\-().]{7,20}$/;
        if (!PHONE_REGEX.test(ph)) e.phone = 'Enter a valid phone number';
    }

    return e;
};

// ── Password form validation ──────────────────────────────────────────────────
const validatePassword = (pwForm) => {
    const e = {};
    if (!pwForm.current_password?.trim())
        e.current_password = 'Current password is required';

    const np = pwForm.new_password;
    if (!np)              e.new_password = 'New password is required';
    else if (np.length < 8) e.new_password = 'Password must be at least 8 characters';
    else if (!/[a-zA-Z]/.test(np)) e.new_password = 'Password must contain at least one letter';
    else if (!/[0-9]/.test(np))    e.new_password = 'Password must contain at least one number';

    if (!pwForm.confirm)   e.confirm = 'Please confirm your new password';
    else if (pwForm.confirm !== pwForm.new_password) e.confirm = 'Passwords do not match';

    return e;
};

// ── Password strength score 0-3 ───────────────────────────────────────────────
const pwStrength = (pw) => {
    if (!pw) return 0;
    let score = 0;
    if (pw.length >= 8)  score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw) && /[^a-zA-Z0-9]/.test(pw)) score++;
    return Math.min(3, score);
};

const STRENGTH_CONFIG = [
    { color: '#dc2626', label: 'Too short' },
    { color: '#d97706', label: 'Weak'      },
    { color: '#d97706', label: 'Fair'      },
    { color: '#059669', label: 'Strong'    },
];

// ════════════════════════════════════════════════════════════════════════════
const Profile = () => {
    const { user: authUser } = useAuth();

    const [profile, setProfile]  = useState(null);
    const [loading, setLoading]  = useState(true);
    const [loadErr, setLoadErr]  = useState(null);

    // Profile form
    const [form,     setFormState] = useState({ first_name:'', last_name:'', phone:'' });
    const [errors,   setErrors]    = useState({});
    const [attempted,setAttempted] = useState(false);
    const [saving,   setSaving]    = useState(false);

    // Password form
    const [pwForm,     setPwForm]       = useState({ current_password:'', new_password:'', confirm:'' });
    const [pwErrors,   setPwErrors]     = useState({});
    const [pwAttempted,setPwAttempted]  = useState(false);
    const [pwVisible,  setPwVisible]    = useState({ current:false, new:false, confirm:false });
    const [pwSaving,   setPwSaving]     = useState(false);
    const [showPwSection, setShowPwSection] = useState(false);

    const loadProfile = async () => {
        setLoading(true); setLoadErr(null);
        try {
            const res = await axiosInstance.get('/users/profile');
            const p   = res.data.data || res.data;
            setProfile(p);
            setFormState({
                first_name: p.first_name || '',
                last_name:  p.last_name  || '',
                phone:      p.phone      || '',
            });
        } catch (err) {
            setLoadErr(err.response?.data?.message || 'Failed to load profile');
        } finally { setLoading(false); }
    };
    useEffect(() => { loadProfile(); }, []);

    // Update field and clear its error real-time
    const setField = (k, v) => {
        setFormState(p => ({ ...p, [k]: v }));
        if (attempted) setErrors(prev => { const n = {...prev}; delete n[k]; return n; });
    };

    const setPwField = (k, v) => {
        setPwForm(p => ({ ...p, [k]: v }));
        if (pwAttempted) setPwErrors(prev => { const n = {...prev}; delete n[k]; return n; });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setAttempted(true);
        const errs = validateProfile(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            await axiosInstance.put('/users/profile', {
                first_name: form.first_name.trim(),
                last_name:  form.last_name.trim(),
                phone:      form.phone.trim() || null,
            }, { headers: { 'Content-Type': 'application/json' } });
            toast.success('Profile updated successfully!');
            setAttempted(false);
            setErrors({});
            await loadProfile(); // re-fetch to keep avatar/role/company in sync
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save profile');
        } finally { setSaving(false); }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPwAttempted(true);
        const errs = validatePassword(pwForm);
        if (Object.keys(errs).length > 0) { setPwErrors(errs); return; }
        setPwSaving(true);
        try {
            await axiosInstance.patch('/users/profile/password', {
                current_password: pwForm.current_password,
                new_password:     pwForm.new_password,
            });
            toast.success('Password changed successfully!');
            setPwForm({ current_password:'', new_password:'', confirm:'' });
            setPwErrors({});
            setPwAttempted(false);
            setShowPwSection(false);
            await loadProfile();
        } catch (err) {
            // If backend says "incorrect password" show it under current_password field
            const msg = err.response?.data?.message || 'Failed to change password';
            if (msg.toLowerCase().includes('incorrect') || msg.toLowerCase().includes('wrong') || msg.toLowerCase().includes('invalid')) {
                setPwErrors({ current_password: 'Current password is incorrect' });
            } else {
                toast.error(msg);
            }
        } finally { setPwSaving(false); }
    };

    const togglePwVis = (field) =>
        setPwVisible(p => ({ ...p, [field]: !p[field] }));

    const cancelPassword = () => {
        setShowPwSection(false);
        setPwForm({ current_password:'', new_password:'', confirm:'' });
        setPwErrors({});
        setPwAttempted(false);
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight:300 }}>
            <Spinner animation="border" variant="primary"/>
        </div>
    );
    if (loadErr) return (
        <div className="p-4">
            <Alert variant="danger">
                {loadErr}
                <Button variant="link" size="sm" onClick={loadProfile}>Retry</Button>
            </Alert>
        </div>
    );

    const joined   = fmtDate(profile?.join_date || profile?.created_at);
    const roleLabel= profile?.role_name || authUser?.role || '';
    const strength = pwStrength(pwForm.new_password);
    const sc       = STRENGTH_CONFIG[Math.min(strength, 3)];

    // PW fields config for rendering
    const pwFields = [
        { key:'current_password', label:'Current Password',     ph:'Your current password',    toggle:'current' },
        { key:'new_password',     label:'New Password',         ph:'Minimum 8 characters',     toggle:'new'     },
        { key:'confirm',          label:'Confirm New Password', ph:'Repeat new password',       toggle:'confirm' },
    ];

    return (
        <div className="p-3 p-md-4" style={{ maxWidth:1100 }}>
            <h4 className="fw-bold mb-4">My Profile</h4>

            {(profile?.must_change_password === 1 || profile?.must_change_password === true) && (
                <Alert variant="warning" className="d-flex align-items-center gap-2 mb-4">
                    <Key size={16}/>
                    You are using a temporary password. Please change it now for security.
                </Alert>
            )}

            <Row className="g-4">
                {/* ── Left: avatar card ────────────────────────────── */}
                <Col xs={12} md={4}>
                    <Card className="border-0 shadow-sm p-4 text-center h-100">
                        <div className="d-flex justify-content-center mb-3">
                            <AvatarUpload
                                currentAvatar={profile?.avatar}
                                firstName={profile?.first_name}
                                lastName={profile?.last_name}
                                size={96}
                                onUploaded={(newUrl) => {
                                    setProfile(p => ({ ...p, avatar: newUrl }));
                                    toast.success('Profile photo updated!');
                                }}
                            />
                        </div>
                        <h5 className="fw-bold mb-1">
                            {profile?.first_name} {profile?.last_name}
                        </h5>
                        <div className="text-muted small mb-3">{profile?.email}</div>
                        <hr className="my-3"/>
                        <div className="text-start">
                            {profile?.company_name && (
                                <div className="d-flex align-items-center gap-2 mb-2 small">
                                    <Building2 size={14} className="text-muted flex-shrink-0"/>
                                    <span>{profile.company_name}</span>
                                </div>
                            )}
                            {profile?.employee_code && (
                                <div className="d-flex align-items-center gap-2 mb-2 small">
                                    <BadgeCheck size={14} className="text-muted flex-shrink-0"/>
                                    <span className="text-primary fw-medium">{profile.employee_code}</span>
                                </div>
                            )}
                            {profile?.email && (
                                <div className="d-flex align-items-center gap-2 mb-2 small">
                                    <Mail size={14} className="text-muted flex-shrink-0"/>
                                    <span className="text-truncate">{profile.email}</span>
                                </div>
                            )}
                            {joined && (
                                <div className="d-flex align-items-center gap-2 mb-2 small">
                                    <Calendar size={14} className="text-muted flex-shrink-0"/>
                                    <span>Joined {joined}</span>
                                </div>
                            )}
                            {roleLabel && (
                                <div className="mt-3">
                                    <Badge bg="primary" className="px-3 py-1" style={{ fontSize:12 }}>
                                        {roleLabel}
                                    </Badge>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>

                {/* ── Right: edit forms ─────────────────────────────── */}
                <Col xs={12} md={8}>
                    {/* Edit Profile */}
                    <Card className="border-0 shadow-sm mb-4 p-4">
                        <h6 className="fw-bold mb-4 d-flex align-items-center gap-2">
                            <User size={17} className="text-primary"/>Edit Profile
                        </h6>
                        <Form onSubmit={handleSave} noValidate>
                            <Row className="g-3">
                                <Col xs={12} md={6}>
                                    <Form.Label className="small fw-bold">
                                        First Name <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        value={form.first_name}
                                        maxLength={50}
                                        placeholder="First name"
                                        isInvalid={attempted && !!errors.first_name}
                                        onChange={e => setField('first_name', e.target.value)}/>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.first_name}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Label className="small fw-bold">Last Name</Form.Label>
                                    <Form.Control
                                        value={form.last_name}
                                        maxLength={50}
                                        placeholder="Last name"
                                        isInvalid={attempted && !!errors.last_name}
                                        onChange={e => setField('last_name', e.target.value)}/>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.last_name}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold">Email</Form.Label>
                                    <Form.Control
                                        value={profile?.email || ''}
                                        readOnly
                                        style={{ background:'#f8fafc', color:'#6b7280' }}/>
                                    <Form.Text className="text-muted">
                                        Email address cannot be changed.
                                    </Form.Text>
                                </Col>
                                <Col xs={12}>
                                    <Form.Label className="small fw-bold">Phone Number</Form.Label>
                                    <Form.Control
                                        value={form.phone}
                                        placeholder="+91 98765 43210"
                                        type="tel"
                                        maxLength={20}
                                        isInvalid={attempted && !!errors.phone}
                                        onChange={e => setField('phone', e.target.value)}/>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.phone}
                                    </Form.Control.Feedback>
                                </Col>
                            </Row>
                            <Button type="submit" variant="primary"
                                className="mt-4 px-4" disabled={saving}>
                                {saving
                                    ? <><Spinner size="sm" className="me-2"/>Saving…</>
                                    : <><Save size={15} className="me-2"/>Save Changes</>}
                            </Button>
                        </Form>
                    </Card>

                    {/* Change Password */}
                    <Card className="border-0 shadow-sm p-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                <Key size={17} className="text-primary"/>Change Password
                            </h6>
                            <Button variant="outline-secondary" size="sm"
                                onClick={() => showPwSection ? cancelPassword() : setShowPwSection(true)}>
                                {showPwSection ? 'Cancel' : 'Change'}
                            </Button>
                        </div>

                        {showPwSection && (
                            <Form onSubmit={handlePasswordChange} className="mt-4" noValidate>
                                <Row className="g-3">
                                    {pwFields.map(({ key, label, ph, toggle }) => (
                                        <Col key={key} xs={12}>
                                            <Form.Label className="small fw-bold">{label} *</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={pwVisible[toggle] ? 'text' : 'password'}
                                                    value={pwForm[key]}
                                                    placeholder={ph}
                                                    isInvalid={pwAttempted && !!pwErrors[key]}
                                                    onChange={e => setPwField(key, e.target.value)}/>
                                                <Button variant="outline-secondary" type="button"
                                                    onClick={() => togglePwVis(toggle)}>
                                                    {pwVisible[toggle] ? <EyeOff size={15}/> : <Eye size={15}/>}
                                                </Button>
                                                <Form.Control.Feedback type="invalid">
                                                    {pwErrors[key]}
                                                </Form.Control.Feedback>
                                            </InputGroup>

                                            {/* Password strength bar */}
                                            {key === 'new_password' && pwForm.new_password.length > 0 && (
                                                <div className="mt-2">
                                                    <div className="d-flex gap-1 mb-1">
                                                        {[1,2,3].map(i => (
                                                            <div key={i} style={{
                                                                flex:1, height:4, borderRadius:2,
                                                                background: strength >= i ? sc.color : '#e5e7eb',
                                                                transition:'background 0.2s',
                                                            }}/>
                                                        ))}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize:11, color:sc.color }}>
                                                        {pwForm.new_password.length < 8 ? 'Too short' : sc.label}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Live mismatch hint */}
                                            {key === 'confirm'
                                                && pwForm.confirm.length > 0
                                                && pwForm.confirm !== pwForm.new_password
                                                && !pwErrors.confirm && (
                                                <div className="text-danger mt-1" style={{ fontSize:12 }}>
                                                    Passwords do not match
                                                </div>
                                            )}
                                        </Col>
                                    ))}
                                </Row>
                                <div className="mt-1 text-muted" style={{ fontSize:11 }}>
                                    Password must be at least 8 characters and contain a letter and a number.
                                </div>
                                <Button type="submit" variant="primary"
                                    className="mt-3 px-4" disabled={pwSaving}>
                                    {pwSaving
                                        ? <><Spinner size="sm" className="me-2"/>Updating…</>
                                        : <><Key size={15} className="me-2"/>Update Password</>}
                                </Button>
                            </Form>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Profile;
